import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import OptimizedImage from "@/components/OptimizedImage";

const AboutHero = () => {
  const [images, setImages] = useState<string[]>([]);
  const [currentIndices, setCurrentIndices] = useState([0, 1, 2, 3, 4]);
  const [transitionEffects, setTransitionEffects] = useState<Array<'fade' | 'dissolve' | 'slide'>>(['fade', 'dissolve', 'slide', 'fade', 'dissolve']);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchImages = async () => {
      console.log('About Hero: Fetching images from aboutus_images bucket...');
      const { data: files, error } = await supabase
        .storage
        .from('aboutus_images')
        .list();

      if (error) {
        console.error('About Hero: Error fetching images:', error);
        setLoading(false);
        return;
      }

      if (!files || files.length === 0) {
        console.log('About Hero: No images found in bucket');
        setLoading(false);
        return;
      }

      console.log('About Hero: Found files:', files);
      const imageUrls = files
        .filter(file => file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i))
        .map(file => {
          const url = `${supabase.storage.from('aboutus_images').getPublicUrl(file.name).data.publicUrl}`;
          console.log(`About Hero: Generated URL for ${file.name}:`, url);
          return url;
        });

      setImages(imageUrls);
      setLoading(false);
    };

    fetchImages();
  }, []);

  useEffect(() => {
    if (images.length < 5) return;

    const effects: Array<'fade' | 'dissolve' | 'slide'> = ['fade', 'dissolve', 'slide'];
    const timer = setInterval(() => {
      setCurrentIndices(prev => prev.map((_, idx) => 
        (prev[idx] + 1) % images.length
      ));
      setTransitionEffects(prev => prev.map(() => 
        effects[Math.floor(Math.random() * effects.length)]
      ));
    }, 7000);

    return () => clearInterval(timer);
  }, [images.length]);

  const getTransitionClasses = (effect: 'fade' | 'dissolve' | 'slide'): string => {
    const baseClasses = "absolute inset-0 transition-all duration-1000";
    
    switch (effect) {
      case 'fade':
        return `${baseClasses} transition-opacity`;
      case 'dissolve':
        return `${baseClasses} transition-all transform`;
      case 'slide':
        return `${baseClasses} transition-transform`;
      default:
        return baseClasses;
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-96 bg-gray-200 rounded-lg"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-48 bg-gray-200 rounded-lg"></div>
          <div className="h-48 bg-gray-200 rounded-lg"></div>
          <div className="h-48 bg-gray-200 rounded-lg"></div>
          <div className="h-48 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
      <div className="space-y-4">
        <div className="relative aspect-[4/3] rounded-lg shadow-lg overflow-hidden">
          {images.map((image, idx) => (
            <div
              key={image}
              className={`${getTransitionClasses(transitionEffects[0])} ${
                idx === currentIndices[0] ? 'opacity-100 scale-100 translate-x-0' : 
                transitionEffects[0] === 'fade' ? 'opacity-0' :
                transitionEffects[0] === 'dissolve' ? 'opacity-0 scale-95' :
                'opacity-0 translate-x-full'
              }`}
            >
              <OptimizedImage
                src={image}
                alt={`About us ${idx + 1}`}
                className="w-full h-full object-cover"
                width={800}
                height={600}
                priority={idx === currentIndices[0]}
              />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((gridIndex) => (
            <div key={gridIndex} className="relative aspect-square rounded-lg shadow-lg overflow-hidden">
              {images.map((image, idx) => (
                <div
                  key={image}
                  className={`${getTransitionClasses(transitionEffects[gridIndex])} ${
                    idx === currentIndices[gridIndex] ? 'opacity-100 scale-100 translate-x-0' : 
                    transitionEffects[gridIndex] === 'fade' ? 'opacity-0' :
                    transitionEffects[gridIndex] === 'dissolve' ? 'opacity-0 scale-95' :
                    'opacity-0 translate-x-full'
                  }`}
                >
                  <OptimizedImage
                    src={image}
                    alt={`About us ${idx + 1}`}
                    className="w-full h-full object-cover"
                    width={400}
                    height={400}
                    priority={idx === currentIndices[gridIndex]}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AboutHero;